import React, { useState, useEffect } from 'react';
import { 
  X, Search, Upload, Image, File, Video, RefreshCw, Check, Eye, 
  MoreVertical, Edit3, Trash2, FolderPlus, Download, Move,
  AlertCircle, FolderOpen, FileText, Plus
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import useS3Upload from '../hooks/useS3Upload';

const S3AssetBrowser = ({ 
  isOpen, 
  onClose, 
  onSelectAsset, 
  allowedTypes = ['images'], // 'images', 'documents', 'videos', 'ai-generated'
  title = "Browse Assets",
  showUpload = true 
}) => {
  const { token } = useAuth();
  const { uploadFile, uploading, uploadProgress } = useS3Upload();
  
  const [assets, setAssets] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [activeTab, setActiveTab] = useState(allowedTypes[0] || 'images');
  const [currentPath, setCurrentPath] = useState('');
  const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0, asset: null });
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [renameData, setRenameData] = useState({ oldName: '', newName: '' });
  const [deleteData, setDeleteData] = useState({ asset: null, name: '' });
  const [newFolderName, setNewFolderName] = useState('');
  const [isManager, setIsManager] = useState(false);
  
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || (window.location.origin.includes("localhost") ? "http://localhost:8000" : window.location.origin + "/pandvah-backend");

  // Check if user is manager or admin (can manage files)
  useEffect(() => {
    // Get user from auth context - assuming it's available
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setIsManager(['manager', 'admin', 'owner'].includes(user.role));
  }, []);

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClick = () => setContextMenu({ show: false, x: 0, y: 0, asset: null });
    if (contextMenu.show) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [contextMenu.show]);

  // Fetch assets from S3
  const fetchAssets = async (folderType = activeTab, path = currentPath) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('folder_type', folderType);
      params.append('path', path);
      params.append('limit', '100');

      const response = await fetch(`${API_BASE_URL}/api/s3/files?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAssets(data.files || []);
        setFolders(data.folders || []);
      } else {
        console.error('Failed to fetch assets');
        setAssets([]);
        setFolders([]);
      }
    } catch (error) {
      console.error('Error fetching assets:', error);
      setAssets([]);
      setFolders([]);
    } finally {
      setLoading(false);
    }
  };

  // Load assets when component opens or tab changes
  useEffect(() => {
    if (isOpen) {
      fetchAssets(activeTab, currentPath);
    }
  }, [isOpen, activeTab, currentPath]);

  // Filter assets based on search term
  const filteredAssets = assets.filter(asset => {
    const filename = asset.key.split('/').pop().toLowerCase();
    return filename.includes(searchTerm.toLowerCase());
  });

  // Navigate to folder
  const navigateToFolder = (folderName) => {
    const newPath = currentPath ? `${currentPath}/${folderName}` : folderName;
    setCurrentPath(newPath);
  };

  // Navigate back
  const navigateBack = () => {
    if (currentPath) {
      const pathParts = currentPath.split('/');
      pathParts.pop();
      setCurrentPath(pathParts.join('/'));
    }
  };

  // Go to root
  const goToRoot = () => {
    setCurrentPath('');
  };

  // Get breadcrumb path
  const getBreadcrumbs = () => {
    if (!currentPath) return [];
    return currentPath.split('/');
  };

  // Handle file upload
  const handleFileUpload = async (file) => {
    try {
      const result = await uploadFile(file, { folderType: activeTab });
      
      // Add the new asset to the list
      const newAsset = {
        key: result.s3_key,
        url: result.file_url,
        size: result.size,
        last_modified: new Date().toISOString()
      };
      
      setAssets(prev => [newAsset, ...prev]);
      return result;
    } catch (error) {
      console.error('Upload failed:', error);
      throw error;
    }
  };

  // Handle folder upload
  const handleFolderUpload = async (files) => {
    try {
      const formData = new FormData();
      const folderStructure = {};
      
      // Process files and maintain folder structure
      Array.from(files).forEach((file, index) => {
        formData.append('files', file);
        // Get relative path from webkitRelativePath or filename
        const relativePath = file.webkitRelativePath || file.name;
        folderStructure[index.toString()] = relativePath;
      });
      
      formData.append('folder_structure', JSON.stringify(folderStructure));
      formData.append('base_folder_type', activeTab);

      const response = await fetch(`${API_BASE_URL}/api/s3/upload-folder`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const results = await response.json();
        // Refresh assets list
        await fetchAssets(activeTab);
        return results;
      } else {
        throw new Error('Folder upload failed');
      }
    } catch (error) {
      console.error('Folder upload failed:', error);
      throw error;
    }
  };

  // Delete asset
  const deleteAsset = async (asset) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/s3/files/${encodeURIComponent(asset.key)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        // Remove from assets list
        setAssets(prev => prev.filter(a => a.key !== asset.key));
        setContextMenu({ show: false, x: 0, y: 0, asset: null });
        setShowDeleteModal(false);
      } else {
        throw new Error('Delete failed');
      }
    } catch (error) {
      console.error('Delete failed:', error);
      throw error;
    }
  };

  // Rename asset
  const renameAsset = async (oldKey, newName) => {
    try {
      // Construct new key maintaining folder structure
      const keyParts = oldKey.split('/');
      keyParts[keyParts.length - 1] = newName;
      const newKey = keyParts.join('/');

      const response = await fetch(`${API_BASE_URL}/api/s3/rename-file`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          old_key: oldKey,
          new_key: newKey
        })
      });

      if (response.ok) {
        const result = await response.json();
        // Update asset in list
        setAssets(prev => prev.map(asset => 
          asset.key === oldKey 
            ? { ...asset, key: newKey, url: result.new_url }
            : asset
        ));
        setShowRenameModal(false);
        setContextMenu({ show: false, x: 0, y: 0, asset: null });
      } else {
        throw new Error('Rename failed');
      }
    } catch (error) {
      console.error('Rename failed:', error);
      throw error;
    }
  };

  // Create folder
  const createFolder = async (folderName) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/s3/create-folder`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          folder_path: `${activeTab}/${folderName}`
        })
      });

      if (response.ok) {
        // Refresh folders and assets
        await fetchAssets(activeTab);
        setShowCreateFolderModal(false);
        setNewFolderName('');
      } else {
        throw new Error('Folder creation failed');
      }
    } catch (error) {
      console.error('Folder creation failed:', error);
      throw error;
    }
  };

  // Handle asset selection
  const handleSelectAsset = () => {
    if (selectedAsset && onSelectAsset) {
      onSelectAsset({
        url: selectedAsset.url,
        key: selectedAsset.key,
        filename: selectedAsset.key.split('/').pop(),
        size: selectedAsset.size
      });
      onClose();
    }
  };

  // Get file type icon
  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) {
      return <Image className="h-6 w-6" />;
    } else if (['mp4', 'avi', 'mov', 'wmv'].includes(ext)) {
      return <Video className="h-6 w-6" />;
    } else {
      return <File className="h-6 w-6" />;
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Get tab display name
  const getTabDisplayName = (tabId) => {
    const names = {
      'images': 'Images',
      'documents': 'Documents', 
      'videos': 'Videos',
      'ai-generated': 'AI Generated'
    };
    return names[tabId] || tabId;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[80vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 rounded-t-xl" style={{ backgroundColor: 'var(--theme-primary)' }}>
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold text-white">{title}</h3>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 bg-gray-50">
          {allowedTypes.map(type => (
            <button
              key={type}
              onClick={() => {
                setActiveTab(type);
                setCurrentPath(''); // Reset path when changing tabs
              }}
              className={`px-6 py-3 text-sm font-medium border-b-2 ${
                activeTab === type
                  ? 'border-theme-blue text-theme-blue bg-white'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {getTabDisplayName(type)}
            </button>
          ))}
        </div>

        {/* Breadcrumb Navigation */}
        {currentPath && (
          <div className="px-4 py-2 bg-gray-100 border-b border-gray-200">
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <button
                onClick={goToRoot}
                className="hover:text-theme-blue flex items-center space-x-1"
              >
                <FolderOpen className="h-4 w-4" />
                <span>{activeTab}</span>
              </button>
              {getBreadcrumbs().map((crumb, index) => (
                <React.Fragment key={index}>
                  <span className="text-gray-400">/</span>
                  <button
                    onClick={() => {
                      const pathParts = getBreadcrumbs().slice(0, index + 1);
                      setCurrentPath(pathParts.join('/'));
                    }}
                    className="hover:text-theme-blue"
                  >
                    {crumb}
                  </button>
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* Search and Upload */}
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search assets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            {showUpload && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => fetchAssets(activeTab)}
                  disabled={loading}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors flex items-center"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
                
                {isManager && (
                  <button
                    onClick={() => setShowCreateFolderModal(true)}
                    className="px-4 py-2 text-green-600 border border-green-300 rounded-lg hover:bg-green-50 transition-colors flex items-center"
                  >
                    <FolderPlus className="h-4 w-4 mr-2" />
                    New Folder
                  </button>
                )}
                
                <label className="px-4 py-2 bg-theme-blue text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer flex items-center">
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Files
                  <input
                    type="file"
                    multiple
                    accept={activeTab === 'images' ? 'image/*' : activeTab === 'videos' ? 'video/*' : '*/*'}
                    onChange={async (e) => {
                      const files = Array.from(e.target.files);
                      for (const file of files) {
                        try {
                          await handleFileUpload(file);
                        } catch (error) {
                          console.error('Upload failed:', error);
                        }
                      }
                      e.target.value = ''; // Reset input
                    }}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
              </div>
            )}
          </div>
          
          {uploading && (
            <div className="mt-4">
              <div className="flex items-center space-x-2 text-sm text-theme-blue">
                <Upload className="h-4 w-4 animate-pulse" />
                <span>Uploading to S3... {uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                <div 
                  className="bg-theme-blue h-2 rounded-full transition-all duration-300" 
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>

        {/* Assets Grid */}
        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-600">Loading assets...</span>
            </div>
          ) : filteredAssets.length === 0 && folders.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Image className="h-16 w-16 mb-4" />
              <p className="text-lg font-medium">No assets found</p>
              <p className="text-sm">Upload some files to get started</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Folders */}
              {folders.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Folders</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {folders.map((folder, index) => (
                      <div
                        key={index}
                        onClick={() => navigateToFolder(folder)}
                        className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:border-gray-300 hover:shadow-md cursor-pointer transition-all"
                      >
                        <FolderOpen className="h-12 w-12 text-theme-blue mb-2" />
                        <span className="text-sm text-gray-700 text-center truncate w-full" title={folder}>
                          {folder}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Assets */}
              {filteredAssets.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-3">
                    Assets ({filteredAssets.length})
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
              {filteredAssets.map((asset) => {
                const filename = asset.key.split('/').pop();
                const isSelected = selectedAsset?.key === asset.key;
                const isImage = activeTab === 'images';
                
                return (
                  <div
                    key={asset.key}
                    onClick={() => setSelectedAsset(asset)}
                    onContextMenu={(e) => {
                      if (isManager) {
                        e.preventDefault();
                        setContextMenu({
                          show: true,
                          x: e.clientX,
                          y: e.clientY,
                          asset: asset
                        });
                      }
                    }}
                    className={`relative group border-2 rounded-lg p-2 cursor-pointer transition-all duration-200 ${
                      isSelected 
                        ? 'border-theme-blue bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                    }`}
                  >
                    {/* Management menu button for managers */}
                    {isManager && (
                      <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setContextMenu({
                              show: true,
                              x: e.clientX,
                              y: e.clientY,
                              asset: asset
                            });
                          }}
                          className="bg-white shadow-md rounded-full p-1 hover:bg-gray-100 transition-colors"
                        >
                          <MoreVertical className="h-4 w-4 text-gray-600" />
                        </button>
                      </div>
                    )}
                    
                    {/* Selection indicator */}
                    {isSelected && (
                      <div className="absolute top-2 right-2 bg-theme-blue text-white rounded-full p-1 z-10">
                        <Check className="h-3 w-3" />
                      </div>
                    )}
                    
                    {/* Preview */}
                    <div className="aspect-square bg-gray-100 rounded-lg mb-2 overflow-hidden flex items-center justify-center">
                      {isImage ? (
                        <img 
                          src={asset.url} 
                          alt={filename}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : (
                        <div className="text-gray-400">
                          {getFileIcon(filename)}
                        </div>
                      )}
                      
                      {/* Fallback for broken images */}
                      <div className="w-full h-full items-center justify-center text-gray-400 hidden">
                        {getFileIcon(filename)}
                      </div>
                    </div>
                    
                    {/* File info */}
                    <div className="text-xs space-y-1">
                      <p className="font-medium text-gray-800 truncate" title={filename}>
                        {filename}
                      </p>
                      <p className="text-gray-500">
                        {formatFileSize(asset.size)}
                      </p>
                    </div>

                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200 rounded-lg flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <Eye className="h-6 w-6 text-white drop-shadow-lg" />
                      </div>
                    </div>
                  </div>
                );
              })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {filteredAssets.length} asset{filteredAssets.length !== 1 ? 's' : ''} found
              {selectedAsset && (
                <span className="ml-4 font-medium">
                  Selected: {selectedAsset.key.split('/').pop()}
                </span>
              )}
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              
              <button
                onClick={handleSelectAsset}
                disabled={!selectedAsset}
                className="px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ 
                  backgroundColor: selectedAsset ? 'var(--theme-primary)' : '#94a3b8' 
                }}
              >
                Select Asset
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Context Menu */}
      {contextMenu.show && contextMenu.asset && (
        <div
          className="fixed bg-white border border-gray-200 rounded-lg shadow-lg py-2 z-50"
          style={{ 
            left: `${contextMenu.x}px`, 
            top: `${contextMenu.y}px`,
            transform: 'translate(-50%, 0)'
          }}
        >
          <button
            onClick={() => {
              setRenameData({
                oldName: contextMenu.asset.key.split('/').pop(),
                newName: contextMenu.asset.key.split('/').pop()
              });
              setShowRenameModal(true);
            }}
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center"
          >
            <Edit3 className="h-4 w-4 mr-2" />
            Rename
          </button>
          <button
            onClick={() => {
              setDeleteData({
                asset: contextMenu.asset,
                name: contextMenu.asset.key.split('/').pop()
              });
              setShowDeleteModal(true);
            }}
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 text-red-600 flex items-center"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </button>
          <button
            onClick={() => {
              window.open(contextMenu.asset.url, '_blank');
              setContextMenu({ show: false, x: 0, y: 0, asset: null });
            }}
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center"
          >
            <Download className="h-4 w-4 mr-2" />
            Download
          </button>
        </div>
      )}

      {/* Rename Modal */}
      {showRenameModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Rename Asset</h3>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New filename
              </label>
              <input
                type="text"
                value={renameData.newName}
                onChange={(e) => setRenameData(prev => ({ ...prev, newName: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter new filename"
              />
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowRenameModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => renameAsset(contextMenu.asset.key, renameData.newName)}
                disabled={!renameData.newName.trim()}
                className="px-4 py-2 text-white bg-theme-blue rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                Rename
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteData.asset && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center">
                <AlertCircle className="h-6 w-6 text-red-500 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">Delete Asset</h3>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-600">
                Are you sure you want to delete <strong>{deleteData.name}</strong>? This action cannot be undone.
              </p>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteAsset(deleteData.asset)}
                className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Folder Modal */}
      {showCreateFolderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center">
                <FolderPlus className="h-6 w-6 text-green-500 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">Create New Folder</h3>
              </div>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Folder name
              </label>
              <input
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="Enter folder name"
              />
              <p className="text-xs text-gray-500 mt-2">
                Folder will be created in: {activeTab}/{newFolderName}
              </p>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowCreateFolderModal(false);
                  setNewFolderName('');
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => createFolder(newFolderName)}
                disabled={!newFolderName.trim()}
                className="px-4 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                Create Folder
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default S3AssetBrowser;